import { Link, useParams, useNavigate } from 'react-router-dom'
import { useStyle, useDeleteStyle } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { I18nTranslationsCard } from '../components/I18nFieldsSection'
import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

export function StyleDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: style, isLoading, error } = useStyle(id!)
  const deleteMutation = useDeleteStyle()

  const handleDelete = async () => {
    if (!confirm('이 스타일을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return

    try {
      await deleteMutation.mutateAsync(id!)
      toast.success('스타일이 삭제되었습니다')
      navigate('/admin/styles')
    } catch (error: unknown) {
      toast.error(`삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!style) return null

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/styles">스타일</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">{style.name}</h1>
          <p className="text-caption mt-2 leading-relaxed">{style.description}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/styles/${id}/edit`}>
            <Button>
              <Pencil className="w-4 h-4 mr-2" />
              수정
            </Button>
          </Link>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            삭제
          </Button>
        </div>
      </div>

      {/* Main Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">ID</label>
            <div className="mt-1 text-gray-900 font-mono">{style.id}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">정렬 순서</label>
            <div className="mt-1 text-gray-900">{style.listOrder}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">상태</label>
            <div className="mt-1">
              <Badge variant={style.enabled ? 'success' : 'secondary'}>
                {style.enabled ? '활성' : '비활성'}
              </Badge>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">프롬프트 접미사</label>
          <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
            {style.promptSuffix}
          </div>
        </div>

        {style.thumbnailUrl && (
          <div>
            <label className="text-sm font-medium text-gray-700">썸네일</label>
            <div className="mt-1">
              <img
                src={`${style.thumbnailUrl}?v=${new Date(style.updatedAt).getTime()}`}
                alt={style.name}
                className="w-32 h-32 rounded-lg border object-cover"
              />
            </div>
          </div>
        )}
      </div>

      {/* Translations */}
      <I18nTranslationsCard nameI18n={style.nameI18n} descriptionI18n={style.descriptionI18n} />
    </div>
  )
}
